// Copyright (c) 2020-2021 Visual Intelligence LP. All rights reserved.
//
import * as React from 'react';
import { withStyles } from "tss-react/mui";
import { observer } from 'mobx-react';

import Asset, { AssetDTO } from '@shared/models/asset';
import Deliverable, { DeliverableDTO } from '@shared/models/deliverable';
import DeliverablesTable from '@modules/Private/submodules/Orders/components/Deliverable/DeliverablesTable';
import AssetViewing from '@modules/Private/submodules/Orders/components/Asset/AssetViewing';
import { ManageMode } from '@shared/components/Form';
import OrdersStore from '@shared/stores/orders';
import container from '@core/di';
import { ORDER_FIELDS } from '@shared/constants/order';

export interface OrderEntitiesProps {
  className?: string;
  data: {
    asset: Asset;
    deliverables: Array<Deliverable>;
  };
  initializeOrderFormLeaveConfirmation: () => any;
}

@observer
class OrderEntities extends React.Component<OrderEntitiesProps> {
  private store = container.get<OrdersStore>(OrdersStore.diToken);

  private handleDeliverableSubmit = (
    manageMode: ManageMode,
    assetId: AssetDTO['id'],
    deliverableId: DeliverableDTO['id'],
    data: Omit<DeliverableDTO, 'id'>,
  ) => {
    if (manageMode === ManageMode.create) {
      return this.store.setDeliverable(assetId, { ...data, id: deliverableId });
    }

    return this.store.updateDeliverable(assetId, deliverableId, data);
  }

  private handleDeleteDeliverable = (
    assetId: Asset['id'],
    deliverable: { id: DeliverableDTO['id']; status: DeliverableDTO['status'] }
  ) => {
    this.store.removeDeliverable(assetId, deliverable.id);
  }

  render() {
    const { data: { asset, deliverables }, initializeOrderFormLeaveConfirmation } = this.props;
    const classes = withStyles.getClasses(this.props);
    const orderData = this.store.orderDetailsForm.values();
    const orderExpectedDueDate = orderData[ORDER_FIELDS.expectedDueDate];
    const orderDueDate = orderData[ORDER_FIELDS.dueDate];

    return (
      <div className={classes.root}>
        <AssetViewing data={asset} />
        <DeliverablesTable
          assetId={asset.id}
          classes={{ root: classes.table, title: classes.title }}
          data={deliverables}
          managementProps={{
            formComponentProps: {
              onUnmount: initializeOrderFormLeaveConfirmation,
              // ^^^ Form component calls unblock() when closing => 
              // thus terminates history.block handler which was declared in OrderCreating component,
              // there is no way to have multiple history.block handlers https://github.com/ReactTraining/history/issues/423
            },
            maxDueDate: orderExpectedDueDate || orderDueDate,
            defaultDueDate: orderDueDate,
            orderManageMode: ManageMode.create,
            onSubmit: this.handleDeliverableSubmit
          }}
          onDelete={this.handleDeleteDeliverable}
        />
      </div>
    );
  }
}

const OrderEntitiesStyled = withStyles(
  OrderEntities, 
  (theme, props) => ({
    "root": {
      width: '100%',
    },
    "title": {
      fontSize: 17,
      color: theme.palette.colors.black,
    },
    "titleAsset": {
      marginBottom: theme.spacing(4),
    },
    "section": {
      borderRadius: '4px',
      backgroundColor: theme.palette.colors.white,
      padding: theme.spacing(5),
    },
    "assetBlock": {
      borderRadius: '4px',
      width: 'calc(50% - 4px)',
      backgroundColor: theme.palette.colors.white,
      padding: theme.spacing(5),
    },
    "assetField": {
      marginBottom: theme.spacing(4)
    },
    "assetLabel": {
      width: '50%',
      color: theme.palette.colors.greyDark,
      fontSize: 15,
    },
    "assetValue": {
      width: '50%',
      color: theme.palette.colors.black,
      fontSize: 15,
    },
    "table": {
      marginTop: theme.spacing(2),
    }
  })
);

export default OrderEntitiesStyled;